import {
  CutlistOrder,
  CutlistRequestType,
  NestingJobError,
} from '@cutr/constants/cutlist';

import { api, checkError, isAuthorized, logError, serializeCutlist } from './';

export type NestingJobStatus =
  | 'pending'
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'failed';

export type { NestingJobError } from '@cutr/constants/cutlist';

export type NestingJobGroupStatus = {
  groupId: string;
  status: NestingJobStatus;
  error?: NestingJobError;
};

export type PollNestingJobsResponse =
  | { done: false; groups: NestingJobGroupStatus[] }
  | { done: true; groups: NestingJobGroupStatus[]; cutlist: CutlistOrder };

export type TriggerNestingJobsResponse = {
  done: boolean;
  results: { data: { jobId: string; status: string } }[];
};

export const cutlistsApi = {
  duplicateCutlist: (cutlistId: string): Promise<CutlistOrder> => {
    return fetch(api.createUrl(`${cutlistId}/copy`), {
      headers: { ...api.getHeaders() },
      credentials: 'include',
      method: 'POST',
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  createCutlist: (title: string): Promise<CutlistOrder> => {
    const body = JSON.stringify({ title });
    const url = isAuthorized() ? 'new' : 'unauthenticated/new';

    return fetch(api.createUrl(url), {
      headers: { 'Content-Type': 'application/json', ...api.getHeaders() },
      body,
      credentials: 'include',
      method: 'POST',
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  getCutlist: (cutlistId: string): Promise<CutlistOrder> => {
    return fetch(api.createUrl(cutlistId), {
      headers: { ...api.getHeaders() },
      credentials: 'include',
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  getStarterCutlist: (cutlistId: string): Promise<CutlistOrder> => {
    return fetch(api.createUrl(`starter-tier/${cutlistId}`), {
      headers: { ...api.getHeaders() },
      credentials: 'include',
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  deleteCutlist: (cutlistId: string): Promise<Response> => {
    return fetch(api.createUrl(`${cutlistId}`), {
      headers: { ...api.getHeaders() },
      credentials: 'include',
      method: 'DELETE',
    })
      .then(checkError)
      .catch(logError);
  },
  getCutlists: (): Promise<CutlistOrder[]> => {
    return fetch(api.createUrl(), {
      headers: { ...api.getHeaders() },
      credentials: 'include',
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  updateCutlist: (
    requestType: CutlistRequestType,
    submit: boolean,
    cutlistId: string,
    syncThirdParty: boolean
  ): Promise<CutlistOrder> => {
    const url = isAuthorized() ? cutlistId : `unauthenticated/${cutlistId}`;

    const body = JSON.stringify({
      ...serializeCutlist(),
      submit,
      requestType,
      syncThirdParty,
    });
    return fetch(api.createUrl(url), {
      method: 'POST',
      credentials: 'include',
      body,
      headers: {
        'Content-Type': 'application/json',
        ...api.getHeaders(),
      },
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },

  saveStarterTierCutlist: (
    cutlistId: string,
    options: { submit?: boolean; requestType?: CutlistRequestType } = {}
  ): Promise<CutlistOrder> => {
    const body = JSON.stringify({ ...serializeCutlist(), ...options });
    return fetch(api.createUrl(`starter-tier/${cutlistId}`), {
      method: 'POST',
      credentials: 'include',
      body,
      headers: { 'Content-Type': 'application/json', ...api.getHeaders() },
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },

  saveStarterTierLead: (
    cutlistId: string,
    lead: { email: string; phone?: string }
  ): Promise<void> => {
    return fetch(api.createUrl(`starter-tier/${cutlistId}/lead`), {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(lead),
      headers: { 'Content-Type': 'application/json', ...api.getHeaders() },
    })
      .then(checkError)
      .then(() => undefined)
      .catch(logError);
  },

  triggerNestingJobs: (
    cutlistId: string
  ): Promise<TriggerNestingJobsResponse> => {
    return fetch(
      api.createUrl(`starter-tier/${cutlistId}/trigger-nesting-jobs`),
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...api.getHeaders() },
      }
    )
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data);
  },

  pollNestingJobs: (cutlistId: string): Promise<PollNestingJobsResponse> => {
    return fetch(api.createUrl(`starter-tier/${cutlistId}/poll-nesting-jobs`), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...api.getHeaders() },
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data);
  },

  acceptQuote: (cutlistId: string): Promise<CutlistOrder> => {
    return fetch(api.createUrl(`${cutlistId}/accept-quote`), {
      headers: { ...api.getHeaders() },
      credentials: 'include',
      method: 'PUT',
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
};
